import apiClient from "@/services/apiClient";

// `nearby`/`exclude` power the main /events page's default (no-search) listing: page 1 ranks
// by the visitor's IPFlash-resolved city->state->country (see EventController::index), and
// page 2+ drops the cascade and just excludes whatever page 1 already showed (`exclude`,
// comma-separated event IDs) so events don't repeat once the cascade tiers are exhausted.
export async function getEvents({ page = 1, upcoming = true, search = "", per_page, nearby = false, exclude = [] } = {}) {
  const params = { page, upcoming, search, per_page };
  if (nearby) {
    params.nearby = 1;
    if (exclude.length) params.exclude = exclude.join(",");
  }
  const { data } = await apiClient.get("/public/events", { params });
  return data;
}

export async function getEventDetail(eventId) {
  const { data } = await apiClient.get(`/public/events/${eventId}`);
  return data;
}

// Authenticated equivalent of getEventDetail -- routes through the organizer's own
// `/dashboard/events/{event}` (owner-or-admin, see EventController::show) instead of the
// unauthenticated /public proxy. Required for edit/repurpose: a draft (or suspended) event is
// invisible on the public endpoint even to its own owner, since that request carries no auth
// token at all, so the edit page must use this instead or it can never load a draft it just
// created.
export async function getMyEventDetail(eventId) {
  const { data } = await apiClient.get(`/backend/dashboard/events/${eventId}`);
  return data;
}

// Ranked by real demand (bookings + fill rate + featured flag -- see EventController::top on the
// backend), not just recency -- powers the student dashboard's "Top Events" carousel.
export async function getTopEvents(limit = 8) {
  const { data } = await apiClient.get("/public/events/top", { params: { limit } });
  return data;
}

// Events in the student's own country first (soonest first), then other upcoming events filling
// any remaining slots -- see EventController::nearby. Powers "Upcoming Events Around You" on the
// Enrolled Courses page. Authenticated (needs the student's own country), unlike getTopEvents.
export async function getNearbyEvents(limit = 9) {
  const { data } = await apiClient.get("/backend/dashboard/events/nearby", { params: { limit } });
  return data;
}

// `items`: [{ ticketTypeId, quantity }, ...] -- only FREE ticket types may ever be sent here;
// a paid/donation type is purchased through the event's external payment link instead (see
// EventResource.ticket_action) and the backend rejects any paid type that reaches this endpoint.
export async function bookEvent(eventId, { name, email, phone, items } = {}) {
  const { data } = await apiClient.post(`/backend/dashboard/events/${eventId}/book`, { name, email, phone, items });
  return data;
}

// For a visitor who isn't signed in -- routes through the /public proxy (no auth token attached)
// to the guest-booking endpoint (routes/api.php, outside the auth:sanctum group). Phone is
// required here even though it's optional on the authenticated bookEvent() above.
export async function bookEventGuest(eventId, { name, email, phone, items } = {}) {
  const { data } = await apiClient.post(`/public/events/${eventId}/book-guest`, { name, email, phone, items });
  return data;
}

// Second step of the OTP flow: bookEvent()/bookEventGuest() above return
// { requires_verification: true } instead of a booking whenever the platform can't yet vouch for
// the email typed into the form (a signed-in user booking someone else, or any anonymous guest --
// see BookingController::sendBookingOtp on the backend). This confirms the code sent to that
// email and only then actually creates the booking.
export async function verifyBookingOtp(eventId, { email, code } = {}) {
  const { data } = await apiClient.post(`/backend/dashboard/events/${eventId}/book/verify`, { email, code });
  return data;
}

export async function verifyBookingOtpGuest(eventId, { email, code } = {}) {
  const { data } = await apiClient.post(`/public/events/${eventId}/book-guest/verify`, { email, code });
  return data;
}

export async function suspendBooking(bookingId) {
  const { data } = await apiClient.patch(`/backend/dashboard/bookings/${bookingId}/suspend`);
  return data;
}

export async function activateBooking(bookingId) {
  const { data } = await apiClient.patch(`/backend/dashboard/bookings/${bookingId}/activate`);
  return data;
}

export async function deleteBooking(bookingId) {
  const { data } = await apiClient.delete(`/backend/dashboard/bookings/${bookingId}`);
  return data;
}

export async function verifyBooking(bookingCode) {
  const { data } = await apiClient.get(`/public/events/verify/${bookingCode}`);
  return data;
}

// "Find my tickets" -- cross-event lookup by email (see TicketLookupController on the backend).
export async function requestTicketLookupCode(email) {
  const { data } = await apiClient.post("/public/find-tickets/request", { email });
  return data;
}

export async function verifyTicketLookupCode(email, code) {
  const { data } = await apiClient.post("/public/find-tickets/verify", { email, code });
  return data;
}

/**
 * Downloads a booking's ticket PDF and triggers a browser save -- same dedicated-proxy-route
 * approach as certificateService.downloadCertificatePdf, since the generic /backend proxy
 * always parses JSON and can't handle a binary PDF body.
 */
export async function downloadBookingPdf(bookingId, filename) {
  const response = await apiClient.get(`/backend/bookings/${bookingId}/pdf`, {
    responseType: "blob",
  });

  const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || "ticket.pdf";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// Events *I organize* (backend filters by organizer_id) -- distinct from
// getMyEventBookings() below, which is bookings other people made on my events.
export async function getMyEvents({ page = 1, upcoming = true, search = "", per_page } = {}) {
  const { data } = await apiClient.get("/backend/dashboard/my-events", { params: { page, upcoming, search, per_page } });
  return data;
}

export async function getMyEventBookings(page = 1, eventId = null, perPage) {
  const { data } = await apiClient.get("/backend/dashboard/event/bookings", {
    params: { page, event_id: eventId || undefined, per_page: perPage || undefined },
  });
  return data;
}

// Stat summary + ticket-sales-over-time series + status breakdown for ONE event -- powers the
// student dashboard's per-event "Bookings" page (see BookingController::stats on the backend).
export async function getEventBookingStats(eventId) {
  const { data } = await apiClient.get(`/backend/dashboard/event/${eventId}/booking-stats`);
  return data;
}

export async function createEvent(payload) {
  const { data } = await apiClient.post("/backend/dashboard/create-event", payload);
  return data;
}

export async function updateEvent(eventId, payload) {
  const { data } = await apiClient.post(`/backend/dashboard/update-event/${eventId}`, payload);
  return data;
}

export async function repurposeEvent(eventId, payload) {
  const { data } = await apiClient.post(`/backend/dashboard/event/repurpose/${eventId}`, payload);
  return data;
}

// Wizard "Cancel" -- wipes out an in-progress, never-published draft entirely. The backend
// refuses this once the event is actually published, so it can never be used to delete a live
// event with real registrations.
export async function discardDraftEvent(eventId) {
  const { data } = await apiClient.delete(`/backend/dashboard/events/${eventId}/discard-draft`);
  return data;
}

// Backend accepts either a real file upload (not supported through our JSON-only BFF proxy) or a
// base64 string -- send the same data URI on both `base64_file` (satisfies validation) and
// `base64_image` (what the repository actually reads), with `image_type=binary` selecting that path.
export async function uploadEventImage(base64Image, eventId = null) {
  const path = eventId ? `/backend/dashboard/event-image/update/${eventId}` : "/backend/dashboard/event-image/uploads";
  const { data } = await apiClient.post(
    path,
    { base64_file: base64Image, base64_image: base64Image },
    { params: { image_type: "binary" } }
  );
  return data;
}

// Up to 5 base64 images for an event's photo gallery -- returns {filenames, urls}; the
// caller sends `filenames` straight back as the `images` field on the next
// createEvent/updateEvent call, which is what actually persists them onto the event.
// Pass `eventId` when editing an existing event (updates it directly); omit it during the
// create flow (uses the same placeholder-row convention as the single-image upload).
export async function uploadEventGalleryImages(base64Images, eventId = null) {
  const { data } = await apiClient.post("/backend/dashboard/event-gallery/uploads", {
    base64_images: base64Images,
    event_id: eventId,
  });
  return data;
}

// Multi-tier ticket types ("Create tickets") -- see EventTicketTypeController. No
// sales-start/sales-end fields anywhere here, by explicit design.
export async function getEventTicketTypes(eventId) {
  const { data } = await apiClient.get(`/backend/dashboard/events/${eventId}/ticket-types`);
  return data;
}

export async function createEventTicketType(eventId, payload) {
  const { data } = await apiClient.post(`/backend/dashboard/events/${eventId}/ticket-types`, payload);
  return data;
}

export async function updateEventTicketType(eventId, ticketTypeId, payload) {
  const { data } = await apiClient.put(`/backend/dashboard/events/${eventId}/ticket-types/${ticketTypeId}`, payload);
  return data;
}

export async function deleteEventTicketType(eventId, ticketTypeId) {
  const { data } = await apiClient.delete(`/backend/dashboard/events/${eventId}/ticket-types/${ticketTypeId}`);
  return data;
}
